
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUnpaidClients } from '@/hooks/dashboard/useDashboardClients';
import { useClientRemainingAmounts } from '@/hooks/dashboard/useClientRemainingAmounts';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CreditCard, Phone, Mail, ArrowRight } from 'lucide-react';
import { Link } from 'react-router';
import { formatLocalizedDate } from '@/utils/dateHelpers';

const UnpaidClientsList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { data: unpaidClients = [], isLoading } = useUnpaidClients();
  const { data: remainingAmounts = {}, isLoading: isLoadingAmounts } = useClientRemainingAmounts(unpaidClients);

  // Show only first 5 clients for mobile-friendly display
  const displayedClients = unpaidClients.slice(0, 5);
  const hasMore = unpaidClients.length > 5;

  const getPaymentStatusVariant = (status: string) => {
    switch (status) {
      case 'unpaid':
        return 'destructive';
      case 'partial':
        return 'secondary';
      case 'pending':
        return 'outline';
      default:
        return 'destructive';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'unpaid':
        return 'border-l-red-500';
      case 'partial':
        return 'border-l-yellow-500';
      case 'pending':
        return 'border-l-blue-500';
      default:
        return 'border-l-red-500';
    }
  };

  if (isLoading || isLoadingAmounts) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-3">
              <div className="flex justify-between items-start">
                <div className="space-y-2 flex-1">
                  <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-2 bg-gray-200 rounded w-1/2"></div>
                </div>
                <div className="h-5 bg-gray-200 rounded w-12"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (unpaidClients.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>{t('dashboard.noUnpaidClients')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {displayedClients.map((client) => {
        const paymentStatus = client.paymentStatus || 'unpaid';
        const remainingAmountData = remainingAmounts[client.id];
        
        // Use the calculated remaining amount or fallback to subscription total
        const remainingAmount = remainingAmountData?.remainingAmount || 0;
        const totalPaid = remainingAmountData?.totalPaidAmount || 0;
        const totalSubscription = remainingAmountData?.totalSubscriptionAmount || 0;

        return (
          <Link key={client.id} to={`/client/${client.id}`} className="block">
            <Card className={`border-l-4 ${getPaymentStatusColor(paymentStatus)} cursor-pointer hover:shadow-md transition-shadow`}>
              <CardContent className="p-3">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-sm">
                      {client.firstName} {client.lastName}
                    </h3>
                  </div>

                  <div className="text-xs text-muted-foreground space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{t('clients.remainingAmount')}:</span>
                      <span className="font-semibold text-sm text-foreground">€{remainingAmount.toFixed(2)}</span>
                    </div>

                    {totalPaid > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{t('clients.totalPaid')}:</span>
                        <span className="text-green-600 dark:text-green-400">€{totalPaid.toFixed(2)}</span>
                      </div>
                    )}

                    {totalSubscription > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{t('clients.totalAmount')}:</span>
                        <span>€{totalSubscription.toFixed(2)}</span>
                      </div>
                    )}

                    {client.lastPaymentDate && (
                      <div className="flex items-center justify-between">
                        <span className="font-medium">
                          {t('clients.lastPayment')}:
                        </span>
                        <span>
                          {formatLocalizedDate(client.lastPaymentDate, i18n.language)}
                        </span>
                      </div>
                    )}

                    <div className="flex gap-1 mt-3">
                      {client.phone && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          asChild 
                          className="h-7 px-2"
                        >
                          <a
                            href={`tel:${client.phone}`}
                            className="flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Phone className="h-3 w-3" />
                          </a>
                        </Button>
                      )}

                      {client.email && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          asChild 
                          className="h-7 px-2"
                        >
                          <a
                            href={`mailto:${client.email}`}
                            className="flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Mail className="h-3 w-3" />
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        );
      })}
      
      {hasMore && (
        <Button variant="outline" size="sm" asChild className="w-full">
          <Link to="/clients?payment_status=unpaid" className="flex items-center justify-center gap-2">
            <span>{t('dashboard.seeAllUnpaid')}</span>
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      )}
    </div>
  );
};

export default UnpaidClientsList;
